import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import {
    Sparkles, Compass, Mountain, MapPin, ChevronLeft, ChevronRight, X,
    Check, Users,
} from "lucide-react";
import { toast } from "sonner";
import InterestPicker from "../components/InterestPicker";
import { ADVENTURE_STYLES } from "../lib/interests";

const STEPS = [
    { id: "welcome", label: "Welcome" },
    { id: "interests", label: "What do you love?" },
    { id: "style", label: "Your style" },
    { id: "difficulty", label: "Where you thrive" },
    { id: "location", label: "Almost done" },
];

const GROUP_TYPES = ["solo", "friends", "family", "small group", "guided"];
const DIFFICULTIES = ["easy", "moderate", "hard", "expert"];
const TERRAIN = ["mountains", "forest", "desert", "lakes", "rivers", "coast", "plains", "snow"];
const SEASONS = ["spring", "summer", "fall", "winter"];

export default function Onboarding() {
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        interests: [],
        ranked_interests: {},
        custom_interests: [],
        adventure_styles: [],
        preferred_group_type: [],
        preferred_difficulty: [],
        preferred_terrain: [],
        favorite_season: [],
        location: user?.location || "",
        home_region: user?.home_region || "",
    });

    useEffect(() => {
        // If somehow they reach onboarding already completed, send them home.
        if (user?.onboarding_completed) navigate("/feed");
    }, [user, navigate]);

    const patch = (updates) => setForm((f) => ({ ...f, ...updates }));

    const finish = async ({ skipped }) => {
        setSaving(true);
        try {
            const payload = skipped
                ? { onboarding_completed: true }
                : { ...form, onboarding_completed: true };
            const { data } = await api.patch("/users/me", payload);
            updateUser(data);
            toast.success(skipped ? "We'll tailor your feed as you explore." : "All set — your feed is now tailored.");
            navigate("/feed");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const next = () => {
        if (step >= STEPS.length - 1) finish({ skipped: false });
        else setStep((s) => s + 1);
    };
    const back = () => setStep((s) => Math.max(0, s - 1));

    const canContinue = useMemo(() => {
        if (step === 0) return true;
        if (step === 1) return form.interests.length > 0 || form.custom_interests.length > 0;
        if (step === 2) return form.adventure_styles.length > 0 || form.preferred_group_type.length > 0;
        if (step === 3) return form.preferred_difficulty.length > 0 || form.preferred_terrain.length > 0;
        return true;
    }, [step, form]);

    return (
        <div data-testid="onboarding-page" className="min-h-screen bg-alpine-mist">
            <header className="sticky top-0 z-10 bg-snowcap border-b border-ridge-line">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-ember-orange" />
                    <span className="font-display font-black text-midnight-pine tracking-tight">RealWorld Outdoors</span>
                    <div className="flex-1 h-1.5 bg-ridge-line rounded-full overflow-hidden mx-3">
                        <div
                            className="h-full bg-ember-orange transition-all duration-500"
                            style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
                            data-testid="onboarding-progress-bar"
                        />
                    </div>
                    <button
                        data-testid="onboarding-skip-btn"
                        onClick={() => finish({ skipped: true })}
                        disabled={saving}
                        className="text-xs font-bold text-slate-gray hover:text-granite inline-flex items-center gap-1"
                    >
                        Skip for now <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            </header>

            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                <div data-testid={`onboarding-step-${STEPS[step].id}`}>
                    {step === 0 && <WelcomeStep name={user?.name} />}
                    {step === 1 && <InterestsStep form={form} patch={patch} />}
                    {step === 2 && <StyleStep form={form} patch={patch} />}
                    {step === 3 && <DifficultyStep form={form} patch={patch} />}
                    {step === 4 && <LocationStep form={form} patch={patch} />}
                </div>

                <div className="mt-10 flex items-center gap-3">
                    {step > 0 && (
                        <button
                            data-testid="onboarding-back-btn"
                            onClick={back}
                            disabled={saving}
                            className="px-4 py-2.5 rounded-md border border-ridge-line text-granite font-bold text-sm hover:bg-snowcap inline-flex items-center gap-1"
                        >
                            <ChevronLeft className="w-4 h-4" /> Back
                        </button>
                    )}
                    <span className="text-xs text-slate-gray ml-auto" data-testid="onboarding-step-counter">
                        Step {step + 1} of {STEPS.length}
                    </span>
                    <button
                        data-testid="onboarding-next-btn"
                        onClick={next}
                        disabled={!canContinue || saving}
                        className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md bg-ember-orange text-white font-bold text-sm hover:bg-ember-dark active:scale-95 disabled:opacity-50 transition-all"
                    >
                        {step === STEPS.length - 1 ? (saving ? "Finishing…" : "Finish") : "Next"} <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------- step components ----------
function StepHeader({ icon: Icon, kicker, title, sub }) {
    return (
        <header className="mb-8">
            <div className="flex items-center gap-2 mb-3">
                <Icon className="w-5 h-5 text-ember-orange" />
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange">{kicker}</span>
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-midnight-pine tracking-tighter leading-[1.05]">{title}</h1>
            {sub && <p className="text-slate-gray mt-3 max-w-xl">{sub}</p>}
        </header>
    );
}

function WelcomeStep({ name }) {
    return (
        <div>
            <StepHeader
                icon={Sparkles}
                kicker="Let's tailor your feed"
                title={`Welcome${name ? `, ${name.split(" ")[0]}` : ""}.`}
                sub="A few quick taps and RealWorld Outdoors will start surfacing adventures you actually want to do. Takes under a minute. You can skip anytime."
            />
            <ul className="space-y-2 text-sm text-granite mt-6">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-moss" /> Pick the activities you love</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-moss" /> Tell us your adventure style</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-moss" /> Mark difficulty and terrain</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-moss" /> Add your home region</li>
            </ul>
        </div>
    );
}

function InterestsStep({ form, patch }) {
    return (
        <div>
            <StepHeader
                icon={Compass}
                kicker="Step 2 of 5"
                title="What do you love doing outdoors?"
                sub="Tap a category to explore, pick what you enjoy, and mark how into each you are. Don't see it? Add your own."
            />
            <InterestPicker
                interests={form.interests}
                ranked={form.ranked_interests}
                custom={form.custom_interests}
                onChange={(next) => patch(next)}
            />
        </div>
    );
}

function StyleStep({ form, patch }) {
    const toggleStyle = (slug) => {
        const has = form.adventure_styles.includes(slug);
        patch({
            adventure_styles: has
                ? form.adventure_styles.filter((s) => s !== slug)
                : [...form.adventure_styles, slug],
        });
    };
    return (
        <div>
            <StepHeader icon={Sparkles} kicker="Step 3 of 5" title="How do you like to experience the outdoors?" sub="Pick the vibes that fit you — this shapes the kind of adventures we surface. Choose as many as you like." />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
                {ADVENTURE_STYLES.map((s) => {
                    const active = form.adventure_styles.includes(s.slug);
                    return (
                        <button
                            key={s.slug}
                            type="button"
                            data-testid={`onboarding-style-${s.slug}`}
                            onClick={() => toggleStyle(s.slug)}
                            className={`p-3 rounded-md text-left transition-all border ${
                                active ? "border-ember-orange bg-ember-orange/5" : "border-ridge-line bg-snowcap hover:border-midnight-pine"
                            }`}
                        >
                            <div className="text-sm font-bold text-midnight-pine">{s.label}</div>
                            {s.desc && <div className="text-[11px] text-slate-gray mt-0.5">{s.desc}</div>}
                        </button>
                    );
                })}
            </div>
            <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-3 flex items-center gap-2">
                    <Users className="w-3.5 h-3.5" /> Who do you usually go with?
                </h3>
                <div className="flex flex-wrap gap-2">
                    {GROUP_TYPES.map((g) => {
                        const active = form.preferred_group_type.includes(g);
                        return (
                            <button
                                key={g}
                                data-testid={`onboarding-group-${g.replace(/\s+/g, "-")}`}
                                onClick={() => patch({
                                    preferred_group_type: active
                                        ? form.preferred_group_type.filter((x) => x !== g)
                                        : [...form.preferred_group_type, g],
                                })}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all capitalize ${
                                    active ? "bg-midnight-pine text-white" : "bg-snowcap text-granite border border-ridge-line hover:border-midnight-pine"
                                }`}
                            >{g}</button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function DifficultyStep({ form, patch }) {
    const togMulti = (key) => (val) => {
        const set = new Set(form[key]);
        if (set.has(val)) set.delete(val); else set.add(val);
        patch({ [key]: Array.from(set) });
    };
    return (
        <div>
            <StepHeader icon={Mountain} kicker="Step 4 of 5" title="Where do you thrive?" sub="Difficulty, terrain, season. Pick anything that feels right." />
            <Section label="Difficulty" testid="onboarding-difficulty">
                {DIFFICULTIES.map((d) => (
                    <Pill
                        key={d}
                        label={d}
                        testid={`onboarding-difficulty-${d}`}
                        active={form.preferred_difficulty.includes(d)}
                        onClick={() => togMulti("preferred_difficulty")(d)}
                    />
                ))}
            </Section>
            <Section label="Terrain" testid="onboarding-terrain">
                {TERRAIN.map((t) => (
                    <Pill
                        key={t}
                        label={t}
                        testid={`onboarding-terrain-${t}`}
                        active={form.preferred_terrain.includes(t)}
                        onClick={() => togMulti("preferred_terrain")(t)}
                    />
                ))}
            </Section>
            <Section label="Favorite seasons" testid="onboarding-season">
                {SEASONS.map((s) => (
                    <Pill
                        key={s}
                        label={s}
                        testid={`onboarding-season-${s}`}
                        active={form.favorite_season.includes(s)}
                        onClick={() => togMulti("favorite_season")(s)}
                    />
                ))}
            </Section>
        </div>
    );
}

function LocationStep({ form, patch }) {
    return (
        <div>
            <StepHeader icon={MapPin} kicker="Step 5 of 5" title="Where do you adventure from?" sub="We'll surface stuff near your home region. You can keep it general — no exact pin needed." />
            <div className="space-y-4 max-w-md">
                <Field label="General location" hint="e.g. Boulder, CO">
                    <input
                        data-testid="onboarding-location-input"
                        value={form.location}
                        onChange={(e) => patch({ location: e.target.value })}
                        placeholder="Your city / town"
                        className="w-full px-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20 text-granite"
                    />
                </Field>
                <Field label="Home region" hint="e.g. Front Range, Colorado">
                    <input
                        data-testid="onboarding-home-region-input"
                        value={form.home_region}
                        onChange={(e) => patch({ home_region: e.target.value })}
                        placeholder="Your broader region"
                        className="w-full px-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20 text-granite"
                    />
                </Field>
            </div>
            <div className="mt-8 p-4 rounded-md bg-ember-orange/10 border border-ember-orange/30 text-sm text-granite">
                <strong className="font-bold">Almost done.</strong> Tap Finish and we'll point your "For You" feed at the activities, terrain, and region you just shared.
            </div>
        </div>
    );
}

// ---------- small helpers ----------
function Field({ label, hint, children }) {
    return (
        <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-1.5 block">{label}</label>
            {hint && <p className="text-xs text-slate-gray mb-2">{hint}</p>}
            {children}
        </div>
    );
}

function Section({ label, testid, children }) {
    return (
        <div className="mb-6" data-testid={testid}>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-2">{label}</h3>
            <div className="flex flex-wrap gap-2">{children}</div>
        </div>
    );
}

function Pill({ label, testid, active, onClick }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all capitalize ${
                active ? "bg-ember-orange text-white" : "bg-snowcap text-granite border border-ridge-line hover:border-ember-orange"
            }`}
        >{label}</button>
    );
}
